import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  VStack,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Spinner,
  Link,
  Input,
  Select,
  FormControl,
  FormLabel,
  Divider,
  SimpleGrid,
  Checkbox,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputLeftElement,
  Collapse,
} from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { ExternalLinkIcon, DownloadIcon, LockIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  FaChartBar,
  FaUserMd,
  FaSignOutAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaClipboardList,
  FaFileSignature,
  FaUsers,
  FaFilter,
  FaInbox,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getTestTypes } from '../../utils/labUtils';
import { exportToCSV, exportToExcel } from '../../utils/exportUtils';
import PhysicianSignatureModal from './PhysicianSignatureModal';

import { getAuthHeaders } from '../../utils/apiAuth';
import { getSubmissionStatusColor as getStatusColor, getSubmissionStatusLabel as getStatusLabel } from '../../utils/statusBadge';
// ── Helpers ─────────────────────────────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '\u2014';
  try { const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(); } catch { return d; }
};

const formatPatientName = (name) => {
  if (!name) return '\u2014';
  const trimmed = name.trim();
  if (trimmed.includes(',')) return trimmed;
  const words = trimmed.split(/\s+/);
  if (words.length <= 1) return trimmed;
  return words[words.length - 1] + ', ' + words.slice(0, -1).join(' ');
};

// ── Stat Card ───────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color }) => (
  <Box
    bg="white"
    p={5}
    borderRadius="xl"
    boxShadow="sm"
    border="1px solid"
    borderColor="gray.100"
  >
    <HStack spacing={4}>
      <Flex w={12} h={12} borderRadius="lg" bg={`${color}.50`} align="center" justify="center" flexShrink={0}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
      </Flex>
      <Box>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1">{value}</Text>
        <Text fontSize="sm" color="gray.500" mt={0.5}>{label}</Text>
      </Box>
    </HStack>
  </Box>
);

// ── Main Component ──────────────────────────────────────────────────────────────

const DoctorDashboard = () => {
  const [doctorSession, setDoctorSession] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [testTypeNames, setTestTypeNames] = useState({});
  const [resolvedTests, setResolvedTests] = useState([]);
  const [editingClientName, setEditingClientName] = useState('');
  const [savingClientName, setSavingClientName] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    patientName: '', accession: '', clientName: '', dob: '',
    collectedFrom: '', collectedTo: '', status: '', testType: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [quickSearch, setQuickSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Table state
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modals
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isKeyModalOpen, onOpen: onKeyModalOpen, onClose: onKeyModalClose } = useDisclosure();
  const { isOpen: isSignOpen, onOpen: onSignOpen, onClose: onSignClose } = useDisclosure();

  // Change key state
  const [currentKey, setCurrentKey] = useState('');
  const [newKey, setNewKey] = useState('');
  const [confirmKey, setConfirmKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  // ── Session & Data Loading ──────────────────────────────────────────────────

  useEffect(() => {
    if (selectedSubmission && isOpen) {
      setEditingClientName(selectedSubmission.client_name ?? doctorSession?.clinic_name ?? '');
    }
  }, [selectedSubmission, doctorSession?.clinic_name, isOpen]);

  // Resolve test names from tests_on_the_script when a submission is selected
  useEffect(() => {
    const resolve = async () => {
      const tests = Array.isArray(selectedSubmission?.tests_on_the_script) ? selectedSubmission.tests_on_the_script : [];
      const ids = tests.map(t => t.id).filter(Boolean);
      if (!ids.length) { setResolvedTests([]); return; }
      const [{ data: testsData }, { data: panelsData }] = await Promise.all([
        supabase.from('tests').select('id, name, code').in('id', ids),
        supabase.from('panels').select('id, name, code').in('id', ids),
      ]);
      setResolvedTests([
        ...(testsData || []).map(t => ({ id: t.id, name: t.name, code: t.code })),
        ...(panelsData || []).map(p => ({ id: p.id, name: p.name, code: p.code })),
      ]);
    };
    if (selectedSubmission) resolve();
    else setResolvedTests([]);
  }, [selectedSubmission]);

  useEffect(() => {
    const sessionData = localStorage.getItem('doctorSession');
    if (!sessionData) { navigate('/doctor/login'); return; }
    try {
      const parsed = JSON.parse(sessionData);
      setDoctorSession(parsed);
      fetchSubmissions(parsed);
      loadTestTypeNames();
    } catch (error) {
      console.error('Error parsing session:', error);
      localStorage.removeItem('doctorSession');
      navigate('/doctor/login');
    }
  }, []);

  const loadTestTypeNames = async () => {
    try {
      const types = await getTestTypes(true);
      const map = {};
      (types || []).forEach((t) => { map[t.id] = t.name || t.id; });
      setTestTypeNames(map);
    } catch (e) {
      console.error('Error loading test types:', e);
    }
  };

  const fetchSubmissions = async (doctor) => {
    setLoading(true);
    try {
      const { data: nameData, error: nameError } = await selectAll(() => supabase
        .from('submissions').select('*')
        .ilike('doctor_name', `%${doctor.full_name}%`)
        .order('submitted_at', { ascending: false }));

      const { data: emailData, error: emailError } = await selectAll(() => supabase
        .from('submissions').select('*')
        .eq('doctor_email', doctor.email)
        .order('submitted_at', { ascending: false }));

      if (nameError || emailError) throw nameError || emailError;

      const combined = [...(nameData || []), ...(emailData || [])];
      const unique = combined.filter((sub, index, self) =>
        index === self.findIndex(s => s.id === sub.id)
      );
      setSubmissions(unique);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load patient results.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const getAccession = (s) => s?.draw_code || (s?.id ? String(s.id).slice(-8) : '\u2014');
  const getClientName = (s) => s.client_name || doctorSession?.clinic_name || '\u2014';

  const isCompleted = (s) => s.status === 'completed';
  const isCancelled = (s) => s.status === 'cancelled';

  // ── Computed Counts ─────────────────────────────────────────────────────────

  const nonCancelledSubmissions = useMemo(() => submissions.filter(s => !isCancelled(s)), [submissions]);
  const completedCount = useMemo(() => submissions.filter(isCompleted).length, [submissions]);
  const inProgressCount = useMemo(() => submissions.filter(s => !isCompleted(s) && !isCancelled(s)).length, [submissions]);
  const awaitingResultsCount = useMemo(() => submissions.filter(s => s.status === 'waiting_on_lab_results').length, [submissions]);
  const pendingApprovalCount = useMemo(() => submissions.filter(s => s.lab_results_url && !s.physician_approved).length, [submissions]);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({ patientName: '', accession: '', clientName: '', dob: '', collectedFrom: '', collectedTo: '', status: '', testType: '' });
    setAppliedFilters(null);
    setQuickSearch('');
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Get base list based on active tab
  const baseList = useMemo(() => {
    switch (tabIndex) {
      case 0: return nonCancelledSubmissions; // All (hide cancelled)
      case 1: return submissions.filter(s => s.lab_results_url && !s.physician_approved); // Pending Approval
      case 2: return submissions.filter(isCompleted); // Completed
      default: return nonCancelledSubmissions;
    }
  }, [submissions, nonCancelledSubmissions, tabIndex]);

  const filteredSubmissions = useMemo(() => {
    let list = baseList;
    const f = appliedFilters;

    if (f) {
      if (f.patientName.trim()) {
        const q = f.patientName.trim().toLowerCase();
        list = list.filter((s) => (s.patient_name || '').toLowerCase().includes(q));
      }
      if (f.accession.trim()) {
        const q = f.accession.trim().toLowerCase();
        list = list.filter((s) => getAccession(s).toLowerCase().includes(q));
      }
      if (f.clientName.trim()) {
        const q = f.clientName.trim().toLowerCase();
        list = list.filter((s) => getClientName(s).toLowerCase().includes(q));
      }
      if (f.dob) {
        list = list.filter((s) => {
          if (!s.patient_dob) return false;
          const subDob = new Date(s.patient_dob).toISOString().slice(0, 10);
          return subDob === f.dob;
        });
      }
      if (f.collectedFrom) {
        const from = new Date(f.collectedFrom); from.setHours(0, 0, 0, 0);
        list = list.filter((s) => s.submitted_at && new Date(s.submitted_at) >= from);
      }
      if (f.collectedTo) {
        const to = new Date(f.collectedTo); to.setHours(23, 59, 59, 999);
        list = list.filter((s) => s.submitted_at && new Date(s.submitted_at) <= to);
      }
      if (f.status) list = list.filter((s) => s.status === f.status);
      if (f.testType) list = list.filter((s) => s.test_type_id === f.testType);
    }

    if (quickSearch.trim()) {
      const q = quickSearch.trim().toLowerCase();
      list = list.filter((s) =>
        (s.patient_name || '').toLowerCase().includes(q) ||
        getAccession(s).toLowerCase().includes(q) ||
        getClientName(s).toLowerCase().includes(q) ||
        (s.status || '').replace(/_/g, ' ').toLowerCase().includes(q)
      );
    }

    return list;
  }, [baseList, appliedFilters, quickSearch, doctorSession?.clinic_name]);

  // ── Sorting ─────────────────────────────────────────────────────────────────

  const sortedSubmissions = useMemo(() => {
    const arr = [...filteredSubmissions];
    const mult = sortOrder === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va = a[sortBy]; let vb = b[sortBy];
      if (sortBy === 'submitted_at') { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
      if (sortBy === 'patient_name' || sortBy === 'status') { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
      return mult * (va < vb ? -1 : va > vb ? 1 : 0);
    });
    return arr;
  }, [filteredSubmissions, sortBy, sortOrder]);

  // ── Pagination ──────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(sortedSubmissions.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedSubmissions.length);
  const paginatedSubmissions = useMemo(() => sortedSubmissions.slice(startIndex, endIndex), [sortedSubmissions, startIndex, endIndex]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(sortedSubmissions.length / pageSize));
    if (currentPage > total) setCurrentPage(total);
  }, [sortedSubmissions.length, pageSize, currentPage]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortOrder(field === 'submitted_at' ? 'desc' : 'asc'); }
  };

  const SortIndicator = ({ field }) => {
    if (sortBy !== field) return null;
    return <Text as="span" ml={1}>{sortOrder === 'asc' ? '\u2191' : '\u2193'}</Text>;
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleViewDetails = (submission) => { setSelectedSubmission(submission); onOpen(); };

  const handleRowClick = (submission, e) => {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input[type="checkbox"]')) return;
    handleViewDetails(submission);
  };

  const handleLogout = () => { localStorage.removeItem('doctorSession'); navigate('/doctor/login'); };

  // ── Checkbox / Export ───────────────────────────────────────────────────────

  const onToggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onToggleSelectAllPage = (e) => {
    e.stopPropagation();
    const ids = paginatedSubmissions.map((s) => s.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const buildExportRows = (rows) => rows.map((s) => ({
    accession: getAccession(s),
    patient_name: s.patient_name || '',
    client_name: getClientName(s),
    dob: formatDate(s.patient_dob),
    collected: s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '',
    status: s.status ? s.status.replace(/_/g, ' ') : '',
    test_type: s.test_type_id ? (testTypeNames[s.test_type_id] || s.test_type_id) : '',
  }));
  const exportHeaders = ['Accession', 'Patient', 'Client Name', 'DOB', 'Collected', 'Status', 'Test Type'];
  const exportKeys = ['accession', 'patient_name', 'client_name', 'dob', 'collected', 'status', 'test_type'];
  const exportFilename = `doctor-portal-export-${new Date().toISOString().slice(0, 10)}`;

  const onExportSelected = (format) => {
    const rows = sortedSubmissions.filter((s) => selectedIds.has(s.id));
    if (rows.length === 0) { toast({ title: 'No rows selected', status: 'info', duration: 2000 }); return; }
    const mapped = buildExportRows(rows);
    if (format === 'xlsx') exportToExcel(mapped, exportHeaders, exportKeys, exportFilename);
    else exportToCSV(mapped, exportHeaders, exportKeys, exportFilename);
    toast({ title: `Exported ${rows.length} row(s)`, status: 'success', duration: 2000 });
  };

  const handleExportAll = (format) => {
    const mapped = buildExportRows(sortedSubmissions);
    if (format === 'xlsx') exportToExcel(mapped, exportHeaders, exportKeys, exportFilename);
    else exportToCSV(mapped, exportHeaders, exportKeys, exportFilename);
    toast({ title: `Exported ${mapped.length} row(s)`, status: 'success', duration: 2000 });
  };

  // ── Client name save ───────────────────────────────────────────────────────

  const handleSaveClientName = async () => {
    if (!selectedSubmission) return;
    setSavingClientName(true);
    try {
      const value = editingClientName.trim() || null;
      const { error } = await supabase.from('submissions').update({ client_name: value }).eq('id', selectedSubmission.id);
      if (error) throw error;
      setSubmissions((prev) => prev.map((s) => (s.id === selectedSubmission.id ? { ...s, client_name: value } : s)));
      setSelectedSubmission((prev) => (prev ? { ...prev, client_name: value } : null));
      toast({ title: 'Client name saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Failed to save', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSavingClientName(false);
    }
  };

  // ── Change secret key ──────────────────────────────────────────────────────

  const handleChangeKey = async () => {
    if (!currentKey.trim()) { toast({ title: 'Enter your current secret key', status: 'warning', duration: 3000 }); return; }
    if (!newKey.trim()) { toast({ title: 'Enter a new secret key', status: 'warning', duration: 3000 }); return; }
    if (newKey !== confirmKey) { toast({ title: 'New keys do not match', status: 'warning', duration: 3000 }); return; }
    setSavingKey(true);
    try {
      const { data: rows, error: lookupErr } = await supabase.from('doctors').select('id, secret_key').eq('id', doctorSession.id).limit(1);
      if (lookupErr) throw lookupErr;
      const doctor = rows?.[0];
      if (!doctor) throw new Error('Doctor not found');
      if (doctor.secret_key && doctor.secret_key !== currentKey.trim()) {
        toast({ title: 'Current key is incorrect', status: 'error', duration: 4000 }); return;
      }

      const { error: directErr } = await supabase.from('doctors').update({ secret_key: newKey.trim(), updated_at: new Date().toISOString() }).eq('id', doctorSession.id);

      if (directErr) {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/set-doctor-secret-key`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ doctorId: doctorSession.id, secretKey: newKey.trim() }),
        });
        let result;
        try { result = await res.json(); } catch { throw new Error('Unable to update key.'); }
        if (!res.ok) throw new Error(result.error || 'Failed to update key');
      }

      toast({ title: 'Secret key updated', description: 'Use your new key next time you sign in.', status: 'success', duration: 4000 });
      onKeyModalClose(); setCurrentKey(''); setNewKey(''); setConfirmKey('');
    } catch (err) {
      toast({ title: 'Failed to change key', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSavingKey(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.500" fontSize="sm">Loading your dashboard...</Text>
        </VStack>
      </Flex>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box minH="100vh" bg="gray.50">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={3}>
          <Flex justify="space-between" align="center">
            <Image src="/logo.png" alt="AtHomeBloodDraw.com" h={12} objectFit="contain" />

            <HStack spacing={2}>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaClipboardList />}
                onClick={() => navigate('/doctor/order-entry')}
              >
                Order Entry
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaChartBar />}
                onClick={() => navigate('/doctor/analytics')}
              >
                Analytics
              </Button>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="ghost"
                  borderRadius="full"
                  px={0}
                  minW="auto"
                >
                  <Flex
                    w={8}
                    h={8}
                    borderRadius="full"
                    bg="blue.500"
                    align="center"
                    justify="center"
                    _hover={{ bg: 'blue.600' }}
                    transition="background 0.2s"
                  >
                    <Text fontSize="xs" fontWeight="bold" color="white">
                      {doctorSession?.full_name
                        ? doctorSession.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        : 'DR'}
                    </Text>
                  </Flex>
                </MenuButton>
                <MenuList minW="200px">
                  <Box px={3} py={2}>
                    <Text fontSize="sm" fontWeight="semibold">Dr. {doctorSession?.full_name}</Text>
                    {doctorSession?.email && (
                      <Text fontSize="xs" color="gray.500">{doctorSession.email}</Text>
                    )}
                  </Box>
                  <Divider />
                  <MenuItem icon={<LockIcon />} onClick={onKeyModalOpen}>
                    Change Secret Key
                  </MenuItem>
                  <MenuItem icon={<FaSignOutAlt />} onClick={handleLogout} color="red.500">
                    Log Out
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <Container maxW="container.xl" py={6}>
        <VStack spacing={6} align="stretch">

          {/* ── Stat Cards ──────────────────────────────────────────────── */}
          <SimpleGrid columns={{ base: 2, lg: 5 }} spacing={4}>
            <StatCard icon={FaUsers} label="Total Patients" value={nonCancelledSubmissions.length} color="blue" />
            <StatCard icon={FaCheckCircle} label="Completed" value={completedCount} color="green" />
            <StatCard icon={FaHourglassHalf} label="In Progress" value={inProgressCount} color="blue" />
            <StatCard icon={FaClipboardList} label="Awaiting Results" value={awaitingResultsCount} color="purple" />
            <StatCard icon={FaFileSignature} label="Pending Approval" value={pendingApprovalCount} color="orange" />
          </SimpleGrid>

          {/* ── Main Content ────────────────────────────────────────────── */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden" border="1px solid" borderColor="gray.100">
            <Tabs index={tabIndex} onChange={(i) => { setTabIndex(i); setQuickSearch(''); setAppliedFilters(null); setCurrentPage(1); setSelectedIds(new Set()); }} isLazy>
              <Box borderBottom="1px solid" borderColor="gray.100">
                <TabList border="none" px={2} pt={1}>
                  <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500"
                    _selected={{ color: 'blue.600', borderColor: 'blue.500', fontWeight: 'semibold' }}>
                    <Icon as={FaInbox} mr={2} boxSize={3.5} />
                    All Results ({nonCancelledSubmissions.length})
                  </Tab>
                  <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500"
                    _selected={{ color: 'orange.600', borderColor: 'orange.500', fontWeight: 'semibold' }}>
                    <Icon as={FaFileSignature} mr={2} boxSize={3.5} />
                    Pending Approval ({pendingApprovalCount})
                  </Tab>
                  <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500"
                    _selected={{ color: 'green.600', borderColor: 'green.500', fontWeight: 'semibold' }}>
                    <Icon as={FaCheckCircle} mr={2} boxSize={3.5} />
                    Completed ({completedCount})
                  </Tab>
                </TabList>
              </Box>

              {/* Search & Filter Bar */}
              <Box px={5} pt={4} pb={2}>
                <Flex gap={3} align="center" wrap="wrap">
                  <InputGroup flex={1} minW="200px" maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search patient, accession, client..."
                      value={quickSearch}
                      onChange={(e) => { setQuickSearch(e.target.value); setCurrentPage(1); }}
                      borderRadius="lg"
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      _focus={{ bg: 'white', borderColor: 'blue.300' }}
                    />
                  </InputGroup>

                  <Button
                    size="sm"
                    variant={showAdvancedFilters || appliedFilters ? 'solid' : 'outline'}
                    colorScheme={appliedFilters ? 'blue' : 'gray'}
                    leftIcon={<FaFilter />}
                    rightIcon={showAdvancedFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  >
                    Filters {appliedFilters ? '(Active)' : ''}
                  </Button>

                  <Flex flex={1} />

                  {/* Export & Page Size */}
                  <HStack spacing={2}>
                    {selectedIds.size > 0 && (
                      <Menu>
                        <MenuButton as={Button} size="sm" colorScheme="blue" rightIcon={<ChevronDownIcon />}>
                          Export ({selectedIds.size})
                        </MenuButton>
                        <MenuList>
                          <MenuItem onClick={() => onExportSelected('csv')}>CSV</MenuItem>
                          <MenuItem onClick={() => onExportSelected('xlsx')}>Excel</MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                    <Menu>
                      <MenuButton as={Button} size="sm" variant="outline" leftIcon={<DownloadIcon />} rightIcon={<ChevronDownIcon />}>
                        Export All
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleExportAll('csv')}>CSV</MenuItem>
                        <MenuItem onClick={() => handleExportAll('xlsx')}>Excel</MenuItem>
                      </MenuList>
                    </Menu>
                    <Select w="75px" size="sm" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} borderRadius="md">
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Select>
                  </HStack>
                </Flex>

                {/* Collapsible Advanced Filters */}
                <Collapse in={showAdvancedFilters} animateOpacity>
                  <Box mt={4} p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
                    <form onSubmit={handleFilterSubmit}>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Patient Name</FormLabel>
                          <Input size="sm" bg="white" value={filters.patientName} onChange={(e) => handleFilterChange('patientName', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Accession #</FormLabel>
                          <Input size="sm" bg="white" value={filters.accession} onChange={(e) => handleFilterChange('accession', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Client Name</FormLabel>
                          <Input size="sm" bg="white" value={filters.clientName} onChange={(e) => handleFilterChange('clientName', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Date of Birth</FormLabel>
                          <Input size="sm" bg="white" type="date" value={filters.dob} onChange={(e) => handleFilterChange('dob', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Collected From</FormLabel>
                          <Input size="sm" bg="white" type="date" value={filters.collectedFrom} onChange={(e) => handleFilterChange('collectedFrom', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Collected To</FormLabel>
                          <Input size="sm" bg="white" type="date" value={filters.collectedTo} onChange={(e) => handleFilterChange('collectedTo', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Status</FormLabel>
                          <Select size="sm" bg="white" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} placeholder="All">
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500">Test Type</FormLabel>
                          <Select size="sm" bg="white" value={filters.testType} onChange={(e) => handleFilterChange('testType', e.target.value)} placeholder="All">
                            {Object.entries(testTypeNames).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                      <HStack mt={3} spacing={2}>
                        <Button type="submit" size="sm" colorScheme="blue">Apply Filters</Button>
                        <Button size="sm" variant="ghost" onClick={handleFilterClear}>Clear All</Button>
                      </HStack>
                    </form>
                  </Box>
                </Collapse>
              </Box>

              <TabPanels>
                {/* All three tabs share the same table, data is already filtered via baseList */}
                {[0, 1, 2].map((idx) => (
                  <TabPanel key={idx} p={0}>
                    <ResultsTable
                      submissions={paginatedSubmissions}
                      allSubmissions={sortedSubmissions}
                      selectedIds={selectedIds}
                      onToggleSelect={onToggleSelect}
                      onToggleSelectAllPage={onToggleSelectAllPage}
                      onRowClick={handleRowClick}
                      onViewDetails={handleViewDetails}
                      onSort={handleSort}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      getAccession={getAccession}
                      getClientName={getClientName}
                      testTypeNames={testTypeNames}
                      tabIndex={tabIndex}
                      onSignOpen={onSignOpen}
                      setSelectedSubmission={setSelectedSubmission}
                      emptyMessage={
                        tabIndex === 1
                          ? 'No results pending your approval.'
                          : submissions.length === 0
                            ? 'No patient results found. Orders linked to you will appear here once submitted.'
                            : 'No results match your search criteria.'
                      }
                    />
                    {/* Pagination */}
                    {sortedSubmissions.length > 0 && (
                      <HStack justify="space-between" px={5} py={3} borderTop="1px solid" borderColor="gray.100" flexWrap="wrap" gap={2}>
                        <Text fontSize="xs" color="gray.500">
                          Showing {startIndex + 1}{'\u2013'}{endIndex} of {sortedSubmissions.length}
                        </Text>
                        <HStack spacing={1}>
                          <Button size="xs" variant="outline" isDisabled={safePage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</Button>
                          {(() => {
                            const pageStart = Math.max(1, Math.min(safePage - 2, totalPages - 4));
                            const pageEnd = Math.min(totalPages, pageStart + 4);
                            const start = Math.max(1, pageEnd - 4);
                            const pages = [];
                            for (let p = start; p <= pageEnd; p++) pages.push(p);
                            return pages.map((pageNum) => (
                              <Button key={pageNum} size="xs" variant={safePage === pageNum ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setCurrentPage(pageNum)}>
                                {pageNum}
                              </Button>
                            ));
                          })()}
                          <Button size="xs" variant="outline" isDisabled={safePage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
                        </HStack>
                      </HStack>
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>

      {/* ── Submission Details Modal ──────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
            <HStack spacing={3}>
              <Flex w={10} h={10} borderRadius="lg" bg="blue.50" align="center" justify="center">
                <Icon as={FaUserMd} color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="bold">{selectedSubmission?.patient_name}</Text>
                <HStack spacing={2}>
                  {selectedSubmission && (
                    <Badge colorScheme={getStatusColor(selectedSubmission.status)} fontSize="xs" borderRadius="full" px={2}>
                      {getStatusLabel(selectedSubmission.status)}
                    </Badge>
                  )}
                  <Text fontSize="xs" color="gray.400" fontFamily="mono">
                    {selectedSubmission && getAccession(selectedSubmission)}
                  </Text>
                </HStack>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            {selectedSubmission && (
              <VStack spacing={5} align="stretch">
                {/* Patient Info */}
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                    Patient Information
                  </Text>
                  <Box bg="gray.50" borderRadius="lg" p={4} border="1px solid" borderColor="gray.100">
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      <Box><Text fontSize="xs" color="gray.500">Name</Text><Text fontWeight="medium">{selectedSubmission.patient_name}</Text></Box>
                      <Box><Text fontSize="xs" color="gray.500">Date of Birth</Text><Text>{formatDate(selectedSubmission.patient_dob)}</Text></Box>
                      <Box><Text fontSize="xs" color="gray.500">Email</Text><Text>{selectedSubmission.patient_email || '\u2014'}</Text></Box>
                      <Box><Text fontSize="xs" color="gray.500">Address</Text><Text>{selectedSubmission.patient_address || '\u2014'}</Text></Box>
                    </SimpleGrid>
                    <Divider my={3} />
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.500">Client Name</Text>
                      <Text fontSize="sm">{selectedSubmission.client_name || doctorSession?.clinic_name || '\u2014'}</Text>
                    </Box>
                  </Box>
                </Box>

                {/* Test Info */}
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                    Test Information
                  </Text>
                  <Box bg="gray.50" borderRadius="lg" p={4} border="1px solid" borderColor="gray.100">
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      <Box><Text fontSize="xs" color="gray.500">Status</Text><Badge mt={0.5} colorScheme={getStatusColor(selectedSubmission.status)} fontSize="xs" borderRadius="full" px={2}>{getStatusLabel(selectedSubmission.status)}</Badge></Box>
                      <Box gridColumn="1 / -1"><Text fontSize="xs" color="gray.500">Collected</Text><Text>{new Date(selectedSubmission.submitted_at).toLocaleString()}</Text></Box>
                      {selectedSubmission.special_instructions && (
                        <Box gridColumn="1 / -1"><Text fontSize="xs" color="gray.500">Special Instructions</Text><Text fontSize="sm">{selectedSubmission.special_instructions}</Text></Box>
                      )}
                      {selectedSubmission.specimen_type && (
                        <Box>
                          <Text fontSize="xs" color="gray.500">Specimen</Text>
                          {(() => {
                            try {
                              const specs = JSON.parse(selectedSubmission.specimen_type);
                              return Array.isArray(specs) ? specs.map((s, i) => <Text key={i} fontSize="sm">{s.quantity}x {s.type}</Text>) : <Text fontSize="sm">{selectedSubmission.specimen_type}</Text>;
                            } catch { return <Text fontSize="sm">{selectedSubmission.specimen_type}</Text>; }
                          })()}
                        </Box>
                      )}
                      {selectedSubmission.diagnosis && (
                        <Box><Text fontSize="xs" color="gray.500">Diagnosis</Text><Text fontSize="sm">{selectedSubmission.diagnosis}</Text></Box>
                      )}
                    </SimpleGrid>
                    {resolvedTests.length > 0 && (
                      <Box mt={3}>
                        <Text fontSize="xs" color="gray.500" mb={1}>Tests Ordered</Text>
                        <Flex gap={2} flexWrap="wrap">
                          {resolvedTests.map((t) => (
                            <Badge key={t.id} colorScheme="teal" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs">
                              {t.name}{t.code ? ` (${t.code})` : ''}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Lab Results */}
                {selectedSubmission.lab_results_url && (
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Lab Results
                    </Text>
                    <Box bg="green.50" borderRadius="lg" p={4} border="1px solid" borderColor="green.100">
                      <Link href={selectedSubmission.lab_results_url} isExternal _hover={{ textDecoration: 'none' }}>
                        <Button leftIcon={<ExternalLinkIcon />} colorScheme="green" size="sm">Download Results</Button>
                      </Link>
                    </Box>
                  </Box>
                )}

                {/* Physician Approval */}
                {selectedSubmission.lab_results_url && !selectedSubmission.physician_approved && (
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Physician Approval
                    </Text>
                    <Box bg="orange.50" borderRadius="lg" p={4} border="1px solid" borderColor="orange.100">
                      <Text fontSize="sm" mb={3}>Results are ready for your review and signature.</Text>
                      <Button colorScheme="blue" size="sm" onClick={() => { onClose(); onSignOpen(); }}>Approve & Sign</Button>
                    </Box>
                  </Box>
                )}
                {selectedSubmission.physician_approved && (
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Physician Approval
                    </Text>
                    <Box bg="green.50" borderRadius="lg" p={4} border="1px solid" borderColor="green.100">
                      <HStack spacing={3}>
                        <Badge colorScheme="green" fontSize="xs" px={2} py={0.5} borderRadius="full">Approved</Badge>
                        <Text fontSize="sm" color="gray.600">
                          {selectedSubmission.physician_approved_at ? new Date(selectedSubmission.physician_approved_at).toLocaleString() : ''}
                        </Text>
                      </HStack>
                      {selectedSubmission.physician_approval_notes && (
                        <Text fontSize="sm" mt={2} color="gray.500">Notes: {selectedSubmission.physician_approval_notes}</Text>
                      )}
                      {selectedSubmission.physician_signature_url && (
                        <Box mt={2}>
                          <Image src={selectedSubmission.physician_signature_url} alt="Signature" maxH="50px" border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={1} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100">
            <Button onClick={onClose} size="sm">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Physician Signature Modal ────────────────────────────────────────── */}
      <PhysicianSignatureModal
        isOpen={isSignOpen}
        onClose={onSignClose}
        submission={selectedSubmission}
        doctorSession={doctorSession}
        onApproved={(updated) => {
          setSubmissions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
          setSelectedSubmission(updated);
        }}
      />

      {/* ── Change Secret Key Modal ──────────────────────────────────────────── */}
      <Modal isOpen={isKeyModalOpen} onClose={() => { onKeyModalClose(); setCurrentKey(''); setNewKey(''); setConfirmKey(''); }} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="md">Change Secret Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="sm" color="gray.500">
                Enter your current key and choose a new one.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Current Key</FormLabel>
                <Input type="password" size="sm" value={currentKey} onChange={(e) => setCurrentKey(e.target.value)} />
              </FormControl>
              <Divider />
              <FormControl isRequired>
                <FormLabel fontSize="sm">New Key</FormLabel>
                <Input type="password" size="sm" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Confirm New Key</FormLabel>
                <Input
                  type="password" size="sm"
                  value={confirmKey}
                  onChange={(e) => setConfirmKey(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleChangeKey(); } }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" size="sm" mr={2} onClick={() => { onKeyModalClose(); setCurrentKey(''); setNewKey(''); setConfirmKey(''); }}>Cancel</Button>
            <Button colorScheme="blue" size="sm" onClick={handleChangeKey} isLoading={savingKey}>Update Key</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ── Results Table Component ─────────────────────────────────────────────────────

const ResultsTable = ({
  submissions, selectedIds, onToggleSelect, onToggleSelectAllPage,
  onRowClick, onViewDetails, onSort, sortBy, sortOrder,
  getAccession, getClientName, testTypeNames, tabIndex,
  onSignOpen, setSelectedSubmission, emptyMessage,
}) => {
  if (submissions.length === 0) {
    return (
      <Box py={16} textAlign="center">
        <Icon as={tabIndex === 1 ? FaFileSignature : FaInbox} boxSize={16} color="gray.200" mb={4} />
        <Text fontSize="lg" fontWeight="semibold" color="gray.500" mb={1}>
          {tabIndex === 1 ? 'All caught up!' : 'No results'}
        </Text>
        <Text color="gray.400" fontSize="sm" maxW="400px" mx="auto">{emptyMessage}</Text>
      </Box>
    );
  }

  const SortHeader = ({ field, children, ...props }) => (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      _hover={{ bg: 'gray.100' }}
      fontSize="xs"
      color="gray.500"
      textTransform="uppercase"
      letterSpacing="wider"
      {...props}
    >
      {children}
      {sortBy === field && <Text as="span" ml={1}>{sortOrder === 'asc' ? '\u2191' : '\u2193'}</Text>}
    </Th>
  );

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th w="40px">
              <Checkbox
                isChecked={submissions.length > 0 && submissions.every((s) => selectedIds.has(s.id))}
                isIndeterminate={selectedIds.size > 0 && !submissions.every((s) => selectedIds.has(s.id))}
                onChange={onToggleSelectAllPage}
              />
            </Th>
            <SortHeader field="patient_name">Patient</SortHeader>
            <Th fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Client</Th>
            <SortHeader field="submitted_at">Collected</SortHeader>
            <SortHeader field="status">Status</SortHeader>
            <Th fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Results</Th>
            <Th fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Approval</Th>
            <Th fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {submissions.map((s) => (
            <Tr
              key={s.id}
              onClick={(e) => onRowClick(s, e)}
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              bg={selectedIds.has(s.id) ? 'blue.50' : undefined}
              transition="background 0.15s"
            >
              <Td w="40px" onClick={(e) => e.stopPropagation()}>
                <Checkbox isChecked={selectedIds.has(s.id)} onChange={(e) => onToggleSelect(s.id, e)} />
              </Td>
              <Td>
                <Text fontWeight="medium" color="gray.800">{formatPatientName(s.patient_name)}</Text>
                <Text fontSize="2xs" color="gray.400" fontFamily="mono">{getAccession(s)}</Text>
              </Td>
              <Td><Text fontSize="sm" color="gray.600">{getClientName(s)}</Text></Td>
              <Td><Text fontSize="sm" color="gray.600">{formatDate(s.submitted_at)}</Text></Td>
              <Td>
                <Badge colorScheme={getStatusColor(s.status)} px={2} py={0.5} borderRadius="full" fontSize="2xs" fontWeight="semibold">
                  {getStatusLabel(s.status)}
                </Badge>
              </Td>
              <Td onClick={(e) => e.stopPropagation()}>
                {s.lab_results_url ? (
                  <Link href={s.lab_results_url} isExternal color="blue.500" fontSize="sm" _hover={{ color: 'blue.600' }}>
                    <HStack spacing={1}><DownloadIcon boxSize={3} /><Text fontSize="xs">Download</Text></HStack>
                  </Link>
                ) : (
                  <Text fontSize="xs" color="gray.400">Pending</Text>
                )}
              </Td>
              <Td>
                {s.lab_results_url ? (
                  s.physician_approved ? (
                    <Badge colorScheme="green" fontSize="2xs" borderRadius="full" px={2}>Approved</Badge>
                  ) : (
                    <Badge colorScheme="orange" fontSize="2xs" borderRadius="full" px={2} cursor="pointer" onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSubmission(s);
                      onSignOpen();
                    }}>
                      Sign
                    </Badge>
                  )
                ) : (
                  <Text fontSize="xs" color="gray.300">{'\u2014'}</Text>
                )}
              </Td>
              <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                <HStack spacing={2} justify="flex-end">
                  <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<ExternalLinkIcon />} onClick={() => onViewDetails(s)}>
                    View
                  </Button>
                  {s.lab_results_url && !s.physician_approved && (
                    <Button size="xs" colorScheme="green" onClick={() => { setSelectedSubmission(s); onSignOpen(); }}>
                      Approve
                    </Button>
                  )}
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default DoctorDashboard;
